import React, {useEffect, useRef, useState} from 'react';

import {useLocation, useNavigate, useParams} from 'react-router-dom';

import {Modal, Select, Stack, Radio, Button, Input} from '@mantine/core';
import {notifications} from '@mantine/notifications';
import {useAction, useSelectors, useSelectorWithRefresh} from '@/store/kysely_module';
import FileUpload, {StoredFileMeta, StoredFileMetaWithDurationAndWaveformData} from '@/modules/files/files_module';
import {navigateToPathForReason} from '@/modules/navigation/navigation_module';
import {useModals} from '@/modules/music_sniper/components/modals/modals_provider';
import {useCurrentFileId, useCurrentProjectId, useCurrentWorkspaceId} from '@/store/modules/workspaces_module';
import {IconPaperclip, IconUpload} from '@tabler/icons-react';
import {useInitalFocus} from '@/hooks/useInitalFocus';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {FileSyncManager} from '../../../../../packages/file-sync/file_sync_manager';

type OpenUploadFileModalButtonProps = {
    component?: React.ElementType<{onClick: () => void}>;
};

export const OpenUploadFileModalButton = (props: OpenUploadFileModalButtonProps) => {
    const modals = useModals();

    const currentWorkspaceId = useCurrentWorkspaceId();
    const currentProjectId = useCurrentProjectId();
    const currentFileId = useCurrentFileId();

    const selectors = useSelectors();

    const loc = useLocation();

    // don't show upload file button during mobile share intent flow
    if (loc.pathname.toLowerCase().includes('share')) {
        return null;
    }

    const onClick = async () => {
        let projectId = currentProjectId;
        if (!currentProjectId && currentFileId) {
            const file = await selectors.getFileInfo({file_id: currentFileId}, {workspace_id: currentWorkspaceId});
            projectId = file?.projectId;
        }

        modals.openModal('UploadFileModal', {
            navigateOnSubmit: true,
            workspace_id: currentWorkspaceId,
            project_id: projectId,
        });
    }

    if (props.component) {
        return (
            <props.component
                onClick={onClick}
            />
        );
    }

    return (
        <Button
            id='open-upload-file-modal-button'
            style={{
                marginBottom: '20px',
                // backgroundColor: 'var(--mantine-color-dark-4)',
            }}
            size='xs'
            // variant='outline'
            leftSection={<IconPaperclip size={16} />}
            onClick={onClick}
        >
            Upload New File
        </Button>
    );
};

type Props = {
    data?: {
        workspace_id?: string;
        project_id?: string;
        existingFile?: StoredFileMeta;
        navigateOnSubmit: boolean;
        onSubmit?: () => void;
        title?: string;
    };
    close: () => void;
};

type StoredFormSubmitData = {workspace_id: string; project_id: string};

export const UploadFileModal = (props: Props) => {
    // const [opened, { open, close }] = useDisclosure(false);
    const [storedFormSubmitData, setStoredFormSubmitData] = useState<StoredFormSubmitData | null>(null);

    return (
        <Modal
            title={props.data?.title || 'Upload File'}
            opened={Boolean(props.data)}
            onClose={props.close}
            closeOnClickOutside={false}
            withCloseButton={true}
        >
            {props.data && (
                <UploadFileModalForm
                    data={props.data}
                    storedFormSubmitData={storedFormSubmitData}
                    setStoredFormSubmitData={setStoredFormSubmitData}
                    close={props.close}
                />
            )}
        </Modal>
    );
}

type UploadFileModalFormProps = {
    data: {
        workspace_id?: string;
        project_id?: string;
        existingFile?: StoredFileMeta;
        navigateOnSubmit: boolean;
        onSubmit?: () => void;
    };
    storedFormSubmitData: StoredFormSubmitData | null;
    setStoredFormSubmitData: (data: StoredFormSubmitData) => void;
    close: () => void;
}

type StoredFileLike = StoredFileMeta | StoredFileMetaWithDurationAndWaveformData;
// type StoredFileLikeOrNull = StoredFileLike | null;

const UploadFileModalForm = (props: UploadFileModalFormProps) => {
    const [workspaceId, setWorkspaceId] = useState(props.data.workspace_id || props.storedFormSubmitData?.workspace_id);
    const [selectedProjectId, setSelectedProjectId] = useState(props.data.project_id || props.storedFormSubmitData?.project_id);
    const [shouldCreateNewProject, setShouldCreateNewProject] = useState(!props.data.project_id);
    const [newProjectName, setNewProjectName] = useState('');
    const newProjectNameRef = useRef('');
    const [uploadedFile, setUploadedFile] = useState<StoredFileLike | StoredFileLike[] | null>(null);
    const [isProcessingDirectoryUpload, setIsProcessingDirectoryUpload] = useState(false);

    const selectors = useSelectors();

    const actions = {
        createProject: useAction('createProject'),
        addFileToWorkspace: useAction('addFileToWorkspace'),
        addFileToProject: useAction('addFileToProject'),
        addFilesToProject: useAction('addFilesToProject'),
        updateObjectAttribute: useAction('updateObjectAttribute'),
    };

    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const onUpload = async (args: {fileInfo: StoredFileLike | StoredFileLike[]}) => {
        if (!workspaceId) {
            return;
        }

        let projectIdToAttach = selectedProjectId;
        if (shouldCreateNewProject) {
            const newProject = await actions.createProject({name: newProjectNameRef.current, workspace_id: workspaceId}, {workspace_id: workspaceId});
            projectIdToAttach = newProject.id;
        }

        if (!projectIdToAttach) {
            return;
        }

        const actualFileUpload = async (fileInfo: StoredFileLike, shouldAttachToProject: boolean) => {
            const workspaceFile = await actions.addFileToWorkspace({
                name: fileInfo.name,
                fullPath: fileInfo.fullPath,
                mimeType: fileInfo.mimeType,
                size: fileInfo.size,
                hash: fileInfo.hash,
                workspace_id: workspaceId,
                lastModifiedAt: fileInfo.lastModifiedAt,
                projectId: projectIdToAttach,
                avoidCreatingProjectRevision: !shouldAttachToProject,
            }, {workspace_id: workspaceId});

            if (!workspaceFile) {
                notifications.show({
                    title: 'Upload failed',
                    message: 'Failed to add file to workspace. Please try again.',
                    color: 'red',
                });
                return;
            }

            const promises: Promise<void>[] = [];

            if ('duration' in fileInfo && fileInfo.duration) {
                promises.push(actions.updateObjectAttribute({
                    parent_id: workspaceFile.id,
                    parent_type: 'File',
                    attribute: 'duration',
                    value: fileInfo.duration.toString(),
                }, {workspace_id: workspaceId}));
            }

            if ('waveform_data' in fileInfo && fileInfo.waveform_data) {
                promises.push(actions.updateObjectAttribute({
                    parent_id: workspaceFile.id,
                    parent_type: 'File',
                    attribute: 'waveform_data',
                    value: fileInfo.waveform_data,
                }, {workspace_id: workspaceId}));
            }

            await Promise.all(promises);
            return workspaceFile;
        };

        let workspaceFile: Awaited<ReturnType<typeof actualFileUpload>> | undefined;
        if (Array.isArray(args.fileInfo)) {
            // For batch uploads, we still pass the projectId to enable proper file revision tracking,
            // but set shouldAttachToProject to false to avoid creating multiple project revisions during the upload process
            const uploadedFileIds: string[] = [];
            for (const fileInfo of args.fileInfo) {
                const uploadedFile = await actualFileUpload(fileInfo, false);
                if (uploadedFile) {
                    uploadedFileIds.push(uploadedFile.id);
                }
            }

            // This call will create a single project revision for all files
            if (uploadedFileIds.length > 0) {
                await actions.addFilesToProject({
                    file_ids: uploadedFileIds,
                    project_id: projectIdToAttach,
                }, {workspace_id: workspaceId});
            }
        } else {
            // For single file uploads, attach directly to project
            workspaceFile = await actualFileUpload(args.fileInfo, true);
        }

        props.setStoredFormSubmitData({project_id: projectIdToAttach, workspace_id: workspaceId});

        if (props.data.navigateOnSubmit) {
            if (workspaceFile) {
                navigateToPathForReason(navigate, 'navigate_to_file_page_from_upload_file_modal', `/workspaces/${workspaceId}/files/${workspaceFile.id}`);
            } else {
                navigateToPathForReason(navigate, 'navigate_to_project_page_from_upload_file_modal', `/workspaces/${workspaceId}/projects/${projectIdToAttach}`);
                queryClient.invalidateQueries({
                    queryKey: [workspaceId, 'getFilesForProject', {project_id: projectIdToAttach}],
                });
            }
        }
        if (props.data.onSubmit) {
            props.data.onSubmit();
        }
        props.close();
    };

    const onUploadMutation = useMutation({
        mutationFn: async (args: {fileInfo: StoredFileLike | StoredFileLike[]}) => {
            return await onUpload(args);
        },
    });

    const shouldEnableSubmitButton = Boolean(workspaceId && (shouldCreateNewProject ? newProjectName : selectedProjectId) && (props.data.existingFile || uploadedFile));
    const shouldEnableChooseFileButton = Boolean(workspaceId && (shouldCreateNewProject ? true : selectedProjectId) && !uploadedFile);

    const inputRef = useInitalFocus();

    useEffect(() => {
        if (props.data.existingFile) {
            const withoutExtension = props.data.existingFile.name.substring(0, props.data.existingFile.name.lastIndexOf('.'));
            setNewProjectName(withoutExtension);
            newProjectNameRef.current = withoutExtension;
        }
    }, [props.data.existingFile]);

    return (
        <div>
            <div style={{margin: '10px'}}>
                Workspace
                <WorkspaceSelector
                    workspace_id={workspaceId}
                    onChange={wid => {
                        setWorkspaceId(wid);
                        setSelectedProjectId(undefined);
                    }}
                />
            </div>

            <div style={{margin: '10px'}}>
                Project

                <Stack mt='xs' mb='md'>
                    <Radio checked={shouldCreateNewProject} onChange={() => setShouldCreateNewProject(true)} label='Create new project' />
                    <Radio checked={!shouldCreateNewProject} onChange={() => setShouldCreateNewProject(false)} label='Select existing project' />
                </Stack>

                {shouldCreateNewProject ? (
                    <Input
                        id='new-project-name-input'
                        ref={inputRef}
                        type='text'
                        placeholder='New Project Name'
                        value={newProjectName}
                        onChange={e => {
                            setNewProjectName(e.target.value);
                            newProjectNameRef.current = e.target.value;
                        }}
                        minLength={1}
                    />
                ) : (
                    workspaceId ? (
                        <ProjectSelector
                            key={workspaceId}
                            workspace_id={workspaceId}
                            project_id={selectedProjectId}
                            onChange={projectId => setSelectedProjectId(projectId)}
                        />
                    ) : (
                        <Select disabled={true} />
                    )
                )}
            </div>

            <div style={{margin: '10px', marginTop: '20px'}}>
                {props.data.existingFile && workspaceId && (
                    <Button
                        disabled={!shouldEnableSubmitButton || onUploadMutation.isPending}
                        onClick={() => {
                            onUploadMutation.mutate({
                                fileInfo: props.data.existingFile!,
                            });
                        }}
                        loading={onUploadMutation.isPending}
                    >
                        Submit
                    </Button>
                )}

                {!props.data.existingFile && workspaceId && (
                    <div>
                        <FileUpload
                            workspaceId={workspaceId}
                            disabled={!shouldEnableChooseFileButton || onUploadMutation.isPending || isProcessingDirectoryUpload}
                            onUpload={result => {
                                setUploadedFile(result.fileInfo);
                            }}
                            onUploadStart={fileName => {
                                console.log('onUploadStart', fileName);
                                const withoutExtension = fileName.substring(0, fileName.lastIndexOf('.'));
                                if (shouldCreateNewProject && !newProjectName) {
                                    setNewProjectName(withoutExtension);
                                    newProjectNameRef.current = withoutExtension;
                                }
                            }}
                            onDirectoryUploadStart={async args => {
                                setIsProcessingDirectoryUpload(true);
                                let projectId = selectedProjectId;

                                const directoryPath = args.directoryPath;
                                const directoryName = directoryPath.substring(directoryPath.lastIndexOf('/') + 1);
                                if (shouldCreateNewProject && !newProjectName) {
                                    setNewProjectName(directoryName);
                                    newProjectNameRef.current = directoryName;
                                }

                                // we're in the thick of it here. let's check if this "new project name" is already a project
                                // try and correct the user's action in doing so

                                if (!projectId) {
                                    const projects = await selectors.getProjectsForWorkspace({workspace_id: workspaceId!, topLevelOnly: false, includeHidden: false}, {workspace_id: workspaceId!});
                                    const project = projects.find(project => project.name === newProjectNameRef.current);
                                    if (project) {
                                        setSelectedProjectId(project.id);
                                    }

                                    projectId = project?.id;
                                }

                                if (!projectId) {
                                    // we're going to create a new project, so let's unconditionally include all files in this upload
                                    return args.hashDatas.map(hashData => hashData.relativePath);
                                }

                                const fileSyncManager = new FileSyncManager(selectors);
                                const filePaths = await fileSyncManager.filterFilesToUploadBasedOnHashes({
                                    directoryPath: args.directoryPath,
                                    hashDatas: args.hashDatas,
                                    workspaceId: workspaceId!,
                                    projectId,
                                });

                                return filePaths;
                            }}
                            onDirectoryUploadEnd={args => {
                                setUploadedFile(args.fileInfos);
                                setIsProcessingDirectoryUpload(false);
                                onUploadMutation.mutate({
                                    fileInfo: args.fileInfos,
                                });
                            }}
                        />

                        <Button
                            id='submit-file-upload-button'
                            size='xs'
                            mt='10px'
                            disabled={!shouldEnableSubmitButton || onUploadMutation.isPending}
                            onClick={() => {
                                onUploadMutation.mutate({
                                    fileInfo: uploadedFile!,
                                });
                            }}
                            loading={onUploadMutation.isPending}
                        >
                            Submit
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

type WorkspaceSelectorProps = {
    workspace_id?: string;
    onChange: (workspaceId: string) => void;
}

const WorkspaceSelector = (props: WorkspaceSelectorProps) => {
    const [workspaces] = useSelectorWithRefresh('getAllWorkspaces', {}, [], {workspace_id: null});

    // TODO: favor the most recent workspaces we've interacted with
    //

    return (
        <Select
            withCheckIcon={false}
            value={props.workspace_id}
            onChange={value => {
                props.onChange(value || '');
            }}
            data={workspaces.map(workspace => ({
                label: `${workspace.name} (${workspace.type.toLowerCase()})`,
                value: workspace.id,
            }))}
        />
    );
};

type ProjectSelectorProps = {
    workspace_id: string;
    project_id?: string;
    onChange: (projectId: string) => void;
};

export const ProjectSelector = (props: ProjectSelectorProps) => {
    const [projects] = useSelectorWithRefresh('getProjectsForWorkspace', {workspace_id: props.workspace_id, topLevelOnly: false, includeHidden: false}, [], {workspace_id: props.workspace_id});

    return (
        <Select
            value={props.project_id}
            withCheckIcon={false}
            onChange={value => props.onChange(value || '')}
            data={projects.map(project => ({
                label: project.name,
                value: project.id,
            }))}
        />
    );
};
